import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, AlertCircle, Building2,
  User, Mail, Lock, Phone, MapPin, CheckCircle2, Users, CreditCard, Upload, Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function RegisterCompany() {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [tier, setTier] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [trialChoice, setTrialChoice] = useState('trial'); // 'trial' or 'pay_now'
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoValid, setPromoValid] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);

  const { registerOrganization } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    country_code: '',
    region: 'UK',
    admin_email: '',
    admin_name: '',
    admin_password: '',
    admin_password_confirm: '',
    ceo_email: '',
    ceo_name: '',
    expected_users: 10,
    agency_type: 'home_care',
    care_levels: [],
    description: '',
  });

  const [currencyInfo, setCurrencyInfo] = useState({ symbol: '$', code: 'USD', rate: 1 });

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Logo file too large (max 5MB)');
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/promo/validate`, {
        code: promoCode.trim().toUpperCase(),
        plan_id: tier?.tier_id || '',
      });
      setPromoValid(data);
    } catch (err) {
      setPromoValid(null);
      setError(err.response?.data?.detail || 'Invalid promo code');
    }
    setPromoChecking(false);
  };

  // Fetch tier whenever expected_users changes
  useEffect(() => {
    const count = parseInt(formData.expected_users) || 1;
    axios.get(`${API_URL}/api/subscription/tier-for-count/${count}`)
      .then(({ data }) => setTier(data))
      .catch(() => {});
  }, [formData.expected_users]);

  // Fetch currency info when country changes
  useEffect(() => {
    if (formData.country_code) {
      axios.get(`${API_URL}/api/subscription/tiers-localized?country_code=${formData.country_code}`)
        .then(({ data }) => {
          setCurrencyInfo({ symbol: data.symbol, code: data.currency, rate: data.rate });
        })
        .catch(() => {});
    }
  }, [formData.country_code]);

  const formatPrice = (usdPrice) => {
    if (!currencyInfo || currencyInfo.code === 'USD') return `$${usdPrice.toFixed(2)}`;
    const converted = usdPrice * currencyInfo.rate;
    return `${currencyInfo.symbol}${converted.toFixed(2)}`;
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.address || !formData.phone) {
      setError('Please fill in all company details');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.admin_email || !formData.admin_name || !formData.admin_password) {
      setError('Please fill in all admin details');
      return false;
    }
    if (formData.admin_password !== formData.admin_password_confirm) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.admin_password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.ceo_email || !formData.ceo_name) {
      setError('Please fill in CEO details');
      return false;
    }
    if (formData.ceo_email === formData.admin_email) {
      setError('CEO email must be different from admin email');
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    const count = parseInt(formData.expected_users);
    if (!count || count < 1) {
      setError('Please enter a valid number of expected users');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3 && validateStep3()) setStep(4);
    else if (step === 4 && validateStep4()) setStep(5);
  };

  const handleSubmit = async () => {
    setError('');
    setIsLoading(true);

    const result = await registerOrganization({
      name: formData.name,
      address: formData.address,
      phone: formData.phone,
      country_code: formData.country_code,
      region: formData.region,
      admin_email: formData.admin_email,
      admin_name: formData.admin_name,
      admin_password: formData.admin_password,
      ceo_email: formData.ceo_email,
      ceo_name: formData.ceo_name,
      expected_users: parseInt(formData.expected_users) || 10,
      agency_type: formData.agency_type,
      care_levels: formData.care_levels,
      description: formData.description
    });

    if (result.success) {
      // Show email warning if CEO invitation email failed
      const regData = result.data;
      if (regData?.email_warning) {
        const fullLink = regData.ceo_invitation?.full_link || regData.ceo_invitation?.invitation_link;
        toast.warning('CEO invitation email could not be sent', {
          description: fullLink
            ? `Please share this link with your CEO manually: ${fullLink}`
            : 'Please share the invitation link from your Team Management page.',
          duration: 15000,
          action: fullLink ? {
            label: 'Copy Link',
            onClick: () => {
              navigator.clipboard.writeText(fullLink);
              toast.success('Invitation link copied to clipboard');
            }
          } : undefined
        });
      } else {
        toast.success('CEO invitation email sent successfully');
      }

      // Upload logo if selected
      if (logoFile) {
        try {
          const logoForm = new FormData();
          logoForm.append('logo', logoFile);
          await axios.post(`${API_URL}/api/org/logo`, logoForm, { withCredentials: true });
        } catch (err) {
          console.error('Logo upload failed, continuing with registration');
        }
      }

      if (trialChoice === 'trial') {
        // Free trial — go straight to dashboard
        navigate('/dashboard/admin');
        setIsLoading(false);
        return;
      }

      // Pay now flow — proceed to payment checkout
      try {
        const { data } = await axios.post(
          `${API_URL}/api/payments/checkout`,
          {
            tier_id: tier?.tier_id || 'tier_1_10',
            origin_url: window.location.origin,
            payment_method: paymentMethod
          },
          { withCredentials: true }
        );
        window.location.href = data.url;
      } catch (err) {
        setError('Failed to initiate payment. Please try again.');
        setIsLoading(false);
      }
    } else {
      setError(result.error);
      setIsLoading(false);
    }
  };

  const totalSteps = 5;

  return (
    <div className="min-h-screen bg-muted py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png" alt="FendanaCura" className="h-10 w-auto" />
            <span className="text-xl font-heading font-bold text-foreground">FendanaCura</span>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2">
            Register Your Agency
          </h1>
          <p className="text-muted-foreground">Set up your care agency in just a few steps</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${step >= s ? 'bg-[#FF6F61] text-[#0B1215]' : 'bg-card text-muted-foreground'}`}
              >
                {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              {s < totalSteps && (
                <div className={`w-8 sm:w-12 h-0.5 ${step > s ? 'bg-[#FF6F61]' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-400 text-sm" data-testid="registration-error">{error}</p>
          </div>
        )}

        {/* Form Card */}
        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8">
          {/* Step 1: Company Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#FF6F61]/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#FF6F61]" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-semibold text-foreground">Company Details</h2>
                  <p className="text-sm text-muted-foreground">Tell us about your agency</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-foreground">Agency Name</Label>
                  <Input id="name" value={formData.name} onChange={(e) => updateForm('name', e.target.value)}
                    placeholder="e.g., Sunshine Care Services"
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
                    data-testid="company-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address" className="text-foreground">Business Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="address" value={formData.address} onChange={(e) => updateForm('address', e.target.value)}
                      placeholder="123 Care Street, City, State"
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground pl-10"
                      data-testid="company-address-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-foreground">Business Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="phone" value={formData.phone} onChange={(e) => updateForm('phone', e.target.value)}
                      placeholder="+1 (555) 000-0000"
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground pl-10"
                      data-testid="company-phone-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Company Logo <span className="text-muted-foreground text-xs">(optional)</span></Label>
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo preview" className="w-14 h-14 rounded-xl object-cover border border-border" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-muted border border-border flex items-center justify-center">
                        <Image className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <label className="cursor-pointer">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
                          <Upload className="w-3.5 h-3.5" />
                          {logoFile ? 'Change Logo' : 'Upload Logo'}
                        </span>
                        <input type="file" accept="image/*" className="hidden" onChange={handleLogoSelect} data-testid="logo-upload-input" />
                      </label>
                      <p className="text-[10px] text-muted-foreground mt-1">PNG, JPG up to 5MB. You can also upload later.</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Country</Label>
                  <select
                    value={formData.country_code}
                    onChange={(e) => updateForm('country_code', e.target.value)}
                    className="w-full rounded-md border border-border bg-muted text-foreground px-3 py-2 text-sm"
                    data-testid="country-select"
                  >
                    <option value="">Select country</option>
                    <option value="GB">United Kingdom</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="AU">Australia</option>
                    <option value="IE">Ireland</option>
                    <option value="NG">Nigeria</option>
                    <option value="GH">Ghana</option>
                    <option value="KE">Kenya</option>
                    <option value="ZA">South Africa</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="NL">Netherlands</option>
                    <option value="ES">Spain</option>
                    <option value="IT">Italy</option>
                    <option value="IN">India</option>
                    <option value="AE">United Arab Emirates</option>
                    <option value="JP">Japan</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Compliance Region</Label>
                  <select
                    value={formData.region}
                    onChange={(e) => updateForm('region', e.target.value)}
                    className="w-full rounded-md border border-border bg-muted text-foreground px-3 py-2 text-sm"
                    data-testid="region-select"
                  >
                    <option value="UK">United Kingdom (CQC + NHS)</option>
                    <option value="IE">Republic of Ireland (HIQA + HSE)</option>
                    <option value="US">United States (HIPAA + CMS)</option>
                    <option value="CA">Canada (PHIPA + Accreditation Canada)</option>
                    <option value="AU">Australia (ACQSC + NDIS)</option>
                    <option value="NZ">New Zealand (HDSS)</option>
                    <option value="EU">European Union (EU GDPR)</option>
                  </select>
                  <p className="text-[10px] text-muted-foreground">Sets your compliance frameworks, training requirements, and document templates</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Agency Type</Label>
                  <select
                    value={formData.agency_type}
                    onChange={(e) => updateForm('agency_type', e.target.value)}
                    className="w-full rounded-md border border-border bg-muted text-foreground px-3 py-2 text-sm"
                    data-testid="agency-type-select"
                  >
                    <option value="home_care">Home Care</option>
                    <option value="respite_hospice">Respite / Hospice</option>
                    <option value="residential_nursing">Residential / Nursing Home</option>
                    <option value="clinics">Clinics</option>
                    <option value="hospital">Hospital</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Levels of Care Provided <span className="text-muted-foreground text-xs">(select all that apply)</span></Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'companionship', label: 'Companionship' },
                      { value: 'light_house_duty', label: 'Light House Duty' },
                      { value: 'personal_care', label: 'Personal Care' },
                      { value: 'wound_care', label: 'Wound Care' },
                      { value: 'medication_administration', label: 'Medication Admin' },
                      { value: 'palliative_care', label: 'Palliative Care' },
                    ].map(level => (
                      <button
                        key={level.value}
                        type="button"
                        onClick={() => {
                          const current = formData.care_levels || [];
                          const updated = current.includes(level.value)
                            ? current.filter(v => v !== level.value)
                            : [...current, level.value];
                          updateForm('care_levels', updated);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                          (formData.care_levels || []).includes(level.value)
                            ? 'bg-[#00A6A0] text-white'
                            : 'bg-muted border border-border text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid={`care-level-${level.value}`}
                      >
                        {level.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Agency Description <span className="text-muted-foreground text-xs">(optional)</span></Label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => updateForm('description', e.target.value)}
                    placeholder="Describe your agency's services and specializations..."
                    rows={3}
                    className="w-full rounded-md border border-border bg-muted text-foreground placeholder:text-muted-foreground px-3 py-2 text-sm resize-none"
                    data-testid="agency-description-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Admin Info */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#FF6F61]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#FF6F61]" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-semibold text-foreground">Admin Account</h2>
                  <p className="text-sm text-muted-foreground">Create your administrator account</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="admin_name" className="text-foreground">Full Name</Label>
                  <Input id="admin_name" value={formData.admin_name} onChange={(e) => updateForm('admin_name', e.target.value)}
                    placeholder="Your full name"
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
                    data-testid="admin-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin_email" className="text-foreground">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="admin_email" type="email" value={formData.admin_email}
                      onChange={(e) => updateForm('admin_email', e.target.value)}
                      placeholder="admin@youragency.com"
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground pl-10"
                      data-testid="admin-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin_password" className="text-foreground">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="admin_password" type="password" value={formData.admin_password}
                      onChange={(e) => updateForm('admin_password', e.target.value)}
                      placeholder="Minimum 6 characters"
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground pl-10"
                      data-testid="admin-password-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin_password_confirm" className="text-foreground">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="admin_password_confirm" type="password" value={formData.admin_password_confirm}
                      onChange={(e) => updateForm('admin_password_confirm', e.target.value)}
                      placeholder="Confirm your password"
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground pl-10"
                      data-testid="admin-password-confirm-input" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: CEO Info */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#FF6F61]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#FF6F61]" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-semibold text-foreground">Principal Admin (CEO)</h2>
                  <p className="text-sm text-muted-foreground">They will receive an invitation to join</p>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-muted border border-border mb-4">
                <p className="text-sm text-muted-foreground">
                  The Principal Admin will have full control over the organization including the ability to manage admins and delegate authority.
                </p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ceo_name" className="text-foreground">CEO/Principal Name</Label>
                  <Input id="ceo_name" value={formData.ceo_name} onChange={(e) => updateForm('ceo_name', e.target.value)}
                    placeholder="Principal admin's full name"
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
                    data-testid="ceo-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ceo_email" className="text-foreground">CEO/Principal Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="ceo_email" type="email" value={formData.ceo_email}
                      onChange={(e) => updateForm('ceo_email', e.target.value)}
                      placeholder="ceo@youragency.com"
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground pl-10"
                      data-testid="ceo-email-input" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Network Size & Subscription */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#FF6F61]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#FF6F61]" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-semibold text-foreground">Network Size</h2>
                  <p className="text-sm text-muted-foreground">How many users do you expect in your network?</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="expected_users" className="text-foreground">Expected Number of Users</Label>
                  <Input
                    id="expected_users"
                    type="number"
                    min="1"
                    value={formData.expected_users}
                    onChange={(e) => updateForm('expected_users', e.target.value)}
                    placeholder="Enter expected number of users"
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground text-center text-xl"
                    data-testid="expected-users-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    This includes admins, caregivers, clients, and family members
                  </p>
                </div>

                {/* Dynamic Tier Display */}
                {tier && (
                  <div className="mt-6 p-5 rounded-xl border-2 border-[#FF6F61] bg-[#FF6F61]/5" data-testid="tier-display">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-heading font-semibold text-foreground">{tier.name} Plan</h3>
                        <p className="text-sm text-muted-foreground">
                          {tier.min_users}-{tier.max_users >= 99999 ? '100+' : tier.max_users} users
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-3xl font-heading font-bold text-[#FF6F61]">{formatPrice(tier.price)}</span>
                        <span className="text-muted-foreground text-sm">/month</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#A8E6CF]">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Auto-renewable subscription</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#A8E6CF] mt-1">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Tier auto-adjusts if your network grows or shrinks</span>
                    </div>
                  </div>
                )}

                {/* All Tiers Reference */}
                <div className="mt-4 space-y-2">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">All Pricing Tiers</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: '1-10 users', price: 120.99 },
                      { label: '11-50 users', price: 169.99 },
                      { label: '51-100 users', price: 200.00 },
                      { label: '100+ users', price: 239.00 },
                    ].map((t) => (
                      <div key={t.label} className="p-2 rounded-lg bg-muted border border-border text-center">
                        <p className="text-xs text-muted-foreground">{t.label}</p>
                        <p className="text-sm font-semibold text-foreground">{formatPrice(t.price)}/mo</p>
                      </div>
                    ))}
                  </div>
                  <div className="p-2 rounded-lg bg-muted border border-border text-center">
                    <p className="text-xs text-muted-foreground">Individual Invite</p>
                    <p className="text-sm font-semibold text-foreground">{formatPrice(24.99)}/mo</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Trial or Payment */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#FF6F61]/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-[#FF6F61]" />
                </div>
                <div>
                  <h2 className="text-lg font-heading font-semibold text-foreground">Get Started</h2>
                  <p className="text-sm text-muted-foreground">Choose how you'd like to begin</p>
                </div>
              </div>

              {/* Trial vs Pay Now Choice */}
              <div className="space-y-3">
                <div
                  className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                    trialChoice === 'trial' ? 'border-[#A8E6CF] bg-[#A8E6CF]/5' : 'border-border hover:border-[#384752]'
                  }`}
                  onClick={() => setTrialChoice('trial')}
                  data-testid="choice-free-trial"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      trialChoice === 'trial' ? 'border-[#A8E6CF]' : 'border-[#6B7280]'
                    }`}>
                      {trialChoice === 'trial' && <div className="w-2.5 h-2.5 rounded-full bg-[#A8E6CF]" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-foreground font-semibold">Start 30-Day Free Trial</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#A8E6CF]/20 text-[#A8E6CF] font-medium">Recommended</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        Full access to all features. No credit card required.
                      </p>
                      <div className="p-3 rounded-lg bg-muted/50 border border-border space-y-1.5">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#A8E6CF] flex-shrink-0" />
                          <span>Subscription billing starts only after your trial ends</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#A8E6CF] flex-shrink-0" />
                          <span>Cancel anytime during the trial — no charges</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#A8E6CF] flex-shrink-0" />
                          <span>You'll be notified before billing begins</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                    trialChoice === 'pay_now' ? 'border-[#FF6F61] bg-[#FF6F61]/5' : 'border-border hover:border-[#384752]'
                  }`}
                  onClick={() => setTrialChoice('pay_now')}
                  data-testid="choice-pay-now"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      trialChoice === 'pay_now' ? 'border-[#FF6F61]' : 'border-[#6B7280]'
                    }`}>
                      {trialChoice === 'pay_now' && <div className="w-2.5 h-2.5 rounded-full bg-[#FF6F61]" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-foreground font-semibold mb-1">Add Payment Details Now</h3>
                      <p className="text-sm text-muted-foreground">
                        Set up billing now and your trial starts immediately. You won't be charged until the trial ends.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method Selection - only shown when pay_now */}
              {trialChoice === 'pay_now' && (
                <div className="space-y-3 mt-4">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Payment Method</p>

                  {/* Promo Code */}
                  <div className="p-4 rounded-lg bg-muted/50 border border-border">
                    <Label className="text-foreground text-sm font-medium">Have a promo code?</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        value={promoCode}
                        onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoValid(null); }}
                        placeholder="e.g., XMAS2026"
                        className="bg-muted border-border text-foreground font-mono flex-1"
                        data-testid="promo-code-input"
                      />
                      <Button
                        variant="outline"
                        className="border-[#00A6A0] text-[#00A6A0] hover:bg-[#00A6A0]/10"
                        onClick={validatePromoCode}
                        disabled={promoChecking || !promoCode.trim()}
                        data-testid="apply-promo-btn"
                      >
                        {promoChecking ? 'Checking...' : 'Apply'}
                      </Button>
                    </div>
                    {promoValid && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-[#A8E6CF]">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>
                          {promoValid.discount_type === 'percentage' && `${promoValid.discount_value}% discount applied!`}
                          {promoValid.discount_type === 'free_months' && `${promoValid.discount_value} free months applied!`}
                          {promoValid.discount_type === 'fixed_amount' && `${currencyInfo.symbol}${promoValid.discount_value} off applied!`}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Order Summary */}
                  {tier && (
                    <div className="p-4 rounded-lg bg-muted border border-border" data-testid="order-summary">
                      <h3 className="text-sm font-medium text-muted-foreground mb-3 uppercase tracking-wider">Order Summary</h3>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-foreground">{tier.name} Plan ({formData.expected_users} users)</span>
                        <span className="text-[#FF6F61] font-bold text-xl">{formatPrice(tier.price)}/mo</span>
                      </div>
                      <p className="text-xs text-[#A8E6CF]">30-day free trial included — first charge after trial</p>
                    </div>
                  )}
                  <div
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-4
                      ${paymentMethod === 'stripe' ? 'border-[#FF6F61] bg-[#FF6F61]/5' : 'border-border hover:border-[#384752]'}`}
                    onClick={() => setPaymentMethod('stripe')}
                    data-testid="payment-stripe"
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                      ${paymentMethod === 'stripe' ? 'border-[#FF6F61]' : 'border-[#6B7280]'}`}>
                      {paymentMethod === 'stripe' && <div className="w-2.5 h-2.5 rounded-full bg-[#FF6F61]" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-foreground font-medium">Pay with Stripe</h3>
                      <p className="text-sm text-muted-foreground">Credit/Debit Card, Apple Pay, Google Pay</p>
                    </div>
                    <CreditCard className="w-6 h-6 text-muted-foreground" />
                  </div>

                  <div
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-4
                      ${paymentMethod === 'paypal' ? 'border-[#FF6F61] bg-[#FF6F61]/5' : 'border-border hover:border-[#384752]'}`}
                    onClick={() => setPaymentMethod('paypal')}
                    data-testid="payment-paypal"
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                      ${paymentMethod === 'paypal' ? 'border-[#FF6F61]' : 'border-[#6B7280]'}`}>
                      {paymentMethod === 'paypal' && <div className="w-2.5 h-2.5 rounded-full bg-[#FF6F61]" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-foreground font-medium">Pay with PayPal</h3>
                      <p className="text-sm text-muted-foreground">PayPal balance, bank account, or card</p>
                    </div>
                    <span className="text-muted-foreground font-bold text-sm">PayPal</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            {step > 1 ? (
              <Button variant="ghost" onClick={() => setStep(step - 1)}
                className="text-muted-foreground hover:text-foreground" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />Back
              </Button>
            ) : (
              <Link to="/login">
                <Button variant="ghost" className="text-muted-foreground hover:text-foreground" data-testid="login-link">
                  Already have an account?
                </Button>
              </Link>
            )}

            {step < totalSteps ? (
              <Button onClick={handleNext} className="btn-primary" data-testid="next-btn">
                Continue<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isLoading} className="btn-primary" data-testid="submit-registration-btn">
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="spinner w-4 h-4" />Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {trialChoice === 'trial' ? 'Start Free Trial' : 'Proceed to Payment'}<ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterCompany;
